import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';


function AssignEventsModal({ mod, events, onClose, onSave }) {
  const [selected, setSelected] = useState(mod.assignedEvents || []);
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setSelected(p =>
    p.includes(id) ? p.filter(x => x !== id) : [...p, id]
  );

  const handleSave = async () => {
    setSaving(true);
    await onSave(selected);
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div>
            <div className="section-label">Moderador</div>
            <h2>Eventos asignados a {mod.familyName || mod.email}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {events.length === 0 && (
          <div style={{ color: 'var(--mid)', fontStyle: 'italic', padding: '1rem 0' }}>
            No hay eventos disponibles.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', marginBottom: '1.2rem', maxHeight: 320, overflowY: 'auto' }}>
          {events.map(ev => (
            <label key={ev.id} style={{
              display: 'flex', alignItems: 'center', gap: '.8rem',
              padding: '.7rem .9rem',
              background: selected.includes(ev.id) ? 'rgba(122,171,138,.12)' : 'rgba(255,255,255,.03)',
              border: `1px solid ${selected.includes(ev.id) ? 'rgba(122,171,138,.4)' : 'rgba(122,171,138,.15)'}`,
              borderRadius: 'var(--radius)', cursor: 'pointer', transition: 'all .18s',
            }}>
              <input type="checkbox" checked={selected.includes(ev.id)}
                onChange={() => toggle(ev.id)}
                style={{ accentColor: 'var(--fern)', width: 16, height: 16 }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{ev.name}</div>
                <div style={{ fontSize: '.75rem', color: 'var(--mid)' }}>
                  {new Date(ev.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {ev.location && ` · ${ev.location}`}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '.8rem' }}>
          <button className="btn btn-ghost btn-full" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner" /> Guardando...</> : 'Guardar asignación'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserModal({ user, onClose, onSaved }) {
  const { token } = useAuth();
  const [form, setForm] = useState({
    familyName: user?.familyName || '',
    email: user?.email || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateUser(token, user.uid, form);
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div>
            <div className="section-label">Editar usuario</div>
            <h2>{user.email}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}
        <form onSubmit={save}>
          <div className="form-group">
            <label className="form-label">Nombre</label>
            <input className="form-input" value={form.familyName}
              onChange={e => setForm(p => ({ ...p, familyName: e.target.value }))}
              placeholder="Nombre del cliente" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              required />
          </div>
          <div style={{ display: 'flex', gap: '.8rem', marginTop: '1rem' }}>
            <button type="button" className="btn btn-ghost btn-full" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary btn-full" disabled={saving}>
              {saving ? <><span className="spinner" /> Guardando...</> : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [modalType, setModalType] = useState('family');
  const [form, setForm] = useState({ email: '', familyName: '' });
  const [saving, setSaving] = useState(false);
  const [assigningMod, setAssigningMod] = useState(null); // moderador al que asignar eventos

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.getUsers(token), api.getAdminEvents(token)])
      .then(([u, e]) => { setUsers(u.users || []); setEvents(e.events || []); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => {
    const matchEv = !filterEvent || (u.registeredEvents || []).includes(filterEvent);
    const q = filterSearch.toLowerCase();
    const matchQ = !q || (u.familyName || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    const matchSt = !filterStatus || u.status === filterStatus || (filterStatus === 'pending' && u.mustChangePassword);
    return matchEv && matchQ && matchSt;
  });

  const exportCSV = () => {
    const rows = [['Nombre','Email','Rol','Estado','Último acceso']];
    filtered.forEach(u => rows.push([u.familyName||'', u.email||'', u.role||'', u.mustChangePassword?'Pendiente':'Activo', u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('es-ES') : 'Nunca']));
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `usuarios${filterEvent ? '-' + filterEvent : ''}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (modalType === 'family') await api.createFamilyUser(token, form);
      else if (modalType === 'moderator') await api.request('POST', '/api/admin/users/moderator', form, token);
      else await api.createAdminUser(token, form);
      setShowCreateModal(false); setForm({ email: '', familyName: '' }); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleAssignEvents = async (modId, selectedEvents) => {
    try {
      await api.request('PATCH', `/api/admin/users/${modId}/assigned-events`, { assignedEvents: selectedEvents }, token);
      setAssigningMod(null);
      load();
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.email}? Se borrarán también todas sus reservas.`)) return;
    try {
      await api.deleteUser(token, u.uid);
      load();
    } catch (err) { setError(err.message); }
  };

  if (loading) return <div className="loading-screen" style={{ minHeight: 'auto', padding: '3rem' }}><div className="spinner spinner-lg" /></div>;

  const evMap = {};
  events.forEach(e => { evMap[e.id] = e.name; });

  return (
    <div className="main-content page-enter">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div className="section-label">Administración</div>
          <h1>Usuarios</h1>
          <p style={{ color: 'var(--mid)', fontStyle: 'italic', fontSize: '.95rem', marginTop: '.2rem' }}>Filtra por evento para ver los inscritos</p>
        </div>
        <div style={{ display: 'flex', gap: '.6rem' }}>
          <button className="btn btn-ghost" onClick={() => { setModalType('moderator'); setShowCreateModal(true); }}>+ Moderador</button>
          <button className="btn btn-secondary" onClick={() => { setModalType('admin'); setShowCreateModal(true); }}>+ Admin</button>
          <button className="btn btn-primary" onClick={() => { setModalType('family'); setShowCreateModal(true); }}>+ Cliente</button>
        </div>
      </div>

      {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

      <div className="filter-bar">
        <label>Evento:</label>
        <select value={filterEvent} onChange={e => setFilterEvent(e.target.value)}>
          <option value="">— Todos los eventos —</option>
          {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </select>
        <label>Buscar:</label>
        <input type="text" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="Nombre o email..." />
        <label>Estado:</label>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Todos</option>
          <option value="active">Activo</option>
          <option value="pending">Pendiente contraseña</option>
        </select>
        <button className="btn btn-secondary btn-sm" onClick={exportCSV} style={{ marginLeft: 'auto' }}>⬇ Exportar CSV</button>
      </div>

      <div style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: '.8rem', fontStyle: 'italic' }}>
        {filterEvent
          ? `Mostrando ${filtered.length} usuario${filtered.length !== 1 ? 's' : ''} inscritos en "${evMap[filterEvent] || filterEvent}"`
          : `Mostrando ${filtered.length} usuario${filtered.length !== 1 ? 's' : ''} en total`}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nombre</th><th>Email</th><th>Rol</th><th>Eventos inscritos</th><th>Estado</th><th>Último acceso</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', color: 'var(--mid)', fontStyle: 'italic', padding: '3rem' }}>No se encontraron usuarios.</td></tr>
              ) : filtered.map(u => (
                <tr key={u.uid}>
                  <td style={{ fontWeight: 700 }}>{u.familyName || '—'}</td>
                  <td style={{ fontSize: '.82rem', color: 'var(--mid)' }}>{u.email}</td>
                  <td><span className={`badge ${u.role === 'admin' ? 'badge-warn' : u.role === 'moderator' ? 'badge-indiv' : 'badge-stone'}`}>{u.role === 'admin' ? '⚔ Admin' : u.role === 'moderator' ? '🛡 Mod' : '◈ Cliente'}</span></td>
                  <td>
                    {(u.registeredEvents || []).length === 0
                      ? <span style={{ color: 'var(--mid)', fontStyle: 'italic', fontSize: '.82rem' }}>Ninguno</span>
                      : (u.registeredEvents || []).map(evId => (
                          <span key={evId} className="badge badge-moss" style={{ marginRight: '.25rem' }}>{evMap[evId] || evId}</span>
                        ))}
                  </td>
                  <td>{u.mustChangePassword ? <span className="badge badge-warn">Pendiente</span> : <span className="badge badge-green">Activo</span>}</td>
                  <td style={{ fontSize: '.82rem', color: 'var(--mid)' }}>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('es-ES') : 'Nunca'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '.3rem' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditingUser(u)}>Editar</button>
                      {u.role === 'moderator' && (
                        <button className="btn btn-ghost btn-sm" onClick={() => setAssigningMod(u)}>
                          🏰 Eventos ({(u.assignedEvents || []).length})
                        </button>
                      )}
                      {u.uid !== user?.uid && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(u)}>Eliminar</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal crear usuario */}
      {/* Modal asignación de eventos a moderador */}
      {assigningMod && (
        <AssignEventsModal
          mod={assigningMod}
          events={events}
          onClose={() => setAssigningMod(null)}
          onSave={(selected) => handleAssignEvents(assigningMod.uid, selected)}
        />
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreateModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div>
                <div className="section-label">Nuevo usuario</div>
                <h2>{modalType === 'admin' ? 'Crear administrador' : modalType === 'moderator' ? 'Crear moderador' : 'Crear cuenta de cliente'}</h2>
              </div>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            <div className="alert alert-info"><span>ℹ️</span> Se enviará un email con contraseña temporal al usuario.</div>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label className="form-label">{modalType === 'family' ? 'Nombre de la familia' : 'Nombre'} <span className="req">*</span></label>
                <input className="form-input" value={form.familyName} onChange={e => setForm(p => ({ ...p, familyName: e.target.value }))} required placeholder={modalType === 'family' ? 'Familia García' : 'Nombre completo'} />
              </div>
              <div className="form-group">
                <label className="form-label">Email <span className="req">*</span></label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required placeholder="email@ejemplo.com" />
              </div>
              <div style={{ display: 'flex', gap: '.8rem', marginTop: '1rem' }}>
                <button type="button" className="btn btn-ghost btn-full" onClick={() => setShowCreateModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary btn-full" disabled={saving}>
                  {saving ? <><span className="spinner" /> Creando...</> : 'Crear usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal editar usuario */}
      {editingUser && (
        <UserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); load(); }}
        />
      )}
    </div>
  );
}
